import { useState } from 'react';
import { Calendar, AlertCircle } from 'lucide-react';
import { usePersonStore } from '../../store/personStore';
import type { PersonDetail, Stage, Temperature } from '../../types';
import toast from 'react-hot-toast';
import { TEMPERATURE_STYLES, TEMPERATURE_ORDER } from '../shared/temperatureConfig';

interface PersonPipelineStatusProps {
  person: PersonDetail;
  canEdit?: boolean;
}

// Stage options
const STAGES: { value: Stage; label: string }[] = [
  { value: 'LEAD', label: 'Lead' },
  { value: 'PROSPECT', label: 'Prospect' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'CHURNED', label: 'Churned' },
];

/**
 * Format date for display
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Check if date is overdue
 */
function isOverdue(dateString: string | null): boolean {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

/**
 * Pipeline status card with inline editing
 * Shows stage, temperature, next follow-up, and status
 */
export const PersonPipelineStatus = ({ person, canEdit = true }: PersonPipelineStatusProps) => {
  const { updatePerson } = usePersonStore();
  const [isUpdating, setIsUpdating] = useState(false);

  // Handle stage change
  const handleStageChange = async (newStage: Stage) => {
    if (newStage === person.stage) return;

    setIsUpdating(true);
    try {
      await updatePerson(person.id, { stage: newStage });
      toast.success('Stage updated');
    } catch (error) {
      toast.error('Failed to update stage');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle temperature change
  const handleTemperatureChange = async (newTemp: Temperature | null) => {
    if (newTemp === person.temperature) return;

    setIsUpdating(true);
    try {
      await updatePerson(person.id, { temperature: newTemp });
      toast.success('Temperature updated');
    } catch (error) {
      toast.error('Failed to update temperature');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle next follow-up change
  const handleFollowUpChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value || null;

    setIsUpdating(true);
    try {
      await updatePerson(person.id, { nextFollowUpAt: newDate });
      toast.success('Follow-up date updated');
    } catch (error) {
      toast.error('Failed to update follow-up date');
    } finally {
      setIsUpdating(false);
    }
  };

  const followUpOverdue = isOverdue(person.nextFollowUpAt);

  return (
    <div className="card p-6">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
        Pipeline Status
      </h3>

      <div className="space-y-5">
        {/* Stage */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Stage
          </label>
          <select
            value={person.stage}
            onChange={(e) => handleStageChange(e.target.value as Stage)}
            disabled={isUpdating || !canEdit}
            className="input disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {STAGES.map((stage) => (
              <option key={stage.value} value={stage.value}>
                {stage.label}
              </option>
            ))}
          </select>
        </div>

        {/* Temperature */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Temperature
          </label>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
            {TEMPERATURE_ORDER.map((value) => {
              const style = TEMPERATURE_STYLES[value];
              const { Icon } = style;
              const isSelected = person.temperature === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleTemperatureChange(isSelected ? null : value)}
                  disabled={isUpdating || !canEdit}
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    isSelected
                      ? `${style.selectedBorder} ${style.selectedBg} ${style.selectedText} font-medium`
                      : 'border-gray-200 hover:border-gray-300 text-gray-700 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-300'
                  }`}
                >
                  <Icon className={`h-4 w-4 ${isSelected ? style.iconClass : 'text-gray-400 dark:text-gray-500'}`} />
                  {style.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Next Follow-up */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Next Follow-up</span>
              {followUpOverdue && (
                <span className="flex items-center text-red-600 dark:text-red-400 text-xs">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Overdue
                </span>
              )}
            </div>
          </label>
          <input
            type="date"
            value={person.nextFollowUpAt ? new Date(person.nextFollowUpAt).toISOString().split('T')[0] : ''}
            onChange={handleFollowUpChange}
            disabled={isUpdating || !canEdit}
            className={`input disabled:opacity-50 disabled:cursor-not-allowed ${followUpOverdue ? 'border-red-300 focus:border-red-500 focus:ring-red-500 dark:border-red-600' : ''}`}
          />
        </div>

        {/* Status / Next Action */}
        {person.status && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status / Next Action
            </label>
            <p className="text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 text-sm">
              {person.status}
            </p>
          </div>
        )}

        {/* Source */}
        {person.source && (
          <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Source: <span className="font-medium">{person.source}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
